import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Paper,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Card,
  //CardMedia,
  CardContent,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  //Collapse,
  AppBar,
  Toolbar,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  Menu,
  CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import AddIcon from '@mui/icons-material/Add';
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import ExpandLessIcon from '@mui/icons-material/ExpandLess';
// import ErrorIcon from '@mui/icons-material/Error';
import FolderIcon from '@mui/icons-material/Folder';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PeopleIcon from '@mui/icons-material/People';
import axios from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// const API_URL = 'http://localhost:5005';

// Şifre değiştirme dialog'u
const ChangePasswordDialog = ({ open, onClose }) => {
  const { changePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await changePassword(currentPassword, newPassword);
      if (result) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Şifre Değiştir</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Şifreniz başarıyla değiştirildi!
          </Alert>
        )}
        <TextField
          fullWidth
          label="Mevcut Şifre"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          margin="normal"
        />
        <TextField
          fullWidth
          label="Yeni Şifre"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          margin="normal"
          helperText="En az 6 karakter, bir büyük harf, bir küçük harf, bir rakam ve bir özel karakter içermelidir"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>İptal</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={loading || !currentPassword || !newPassword}
        >
          {loading ? 'Değiştiriliyor...' : 'Değiştir'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Çakışma Dialog bileşeni
const ConflictDialog = ({ open, onClose, file, existingFile, applyToAll, setApplyToAll, setShouldOverwrite, selectedProject }) => {
  const [newImageUrl, setNewImageUrl] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newImageDimensions, setNewImageDimensions] = useState(null);

  useEffect(() => {
    if (file && open) {
      // Yeni resmin URL'ini oluştur
      const url = URL.createObjectURL(file);
      setNewImageUrl(url);

      // Yeni resmin boyutlarını al
      const img = new Image();
      img.onload = () => {
        setNewImageDimensions({
          width: img.width,
          height: img.height
        });
      };
      img.src = url;

      return () => URL.revokeObjectURL(url);
    } else {
      setNewImageUrl(null);
      setNewImageDimensions(null);
    }
  }, [file, open]);

  useEffect(() => {
    if (existingFile?.uuid && open) {
      setLoading(true);
      // Mevcut resmin URL'ini al
      const fetchExistingImage = async () => {
        try {
          console.log('Mevcut resim için istek yapılıyor:', {
            existingFile,
            selectedProject,
            url: `/projects/${selectedProject.id}/images/${existingFile.uuid}`
          });
          const response = await axios.get(`/projects/${selectedProject.id}/images/${existingFile.uuid}`, {
            responseType: 'blob'
          });
          console.log('Resim başarıyla alındı:', response);
          const url = URL.createObjectURL(response.data);
          setExistingImageUrl(url);
        } catch (err) {
          console.error('Mevcut resim yüklenirken hata:', {
            error: err,
            response: err.response,
            existingFile,
            selectedProject
          });
        } finally {
          setLoading(false);
        }
      };
      fetchExistingImage();

      // Cleanup
      return () => {
        if (existingImageUrl) {
          URL.revokeObjectURL(existingImageUrl);
        }
      };
    }
  }, [existingFile?.uuid, selectedProject?.id, open]);

  const handleSkip = () => {
    onClose({ shouldOverwrite: false, applyToAll });
  };

  const handleOverwrite = () => {
    onClose({ shouldOverwrite: true, applyToAll });
  };

  const handleClose = () => {
    onClose({ shouldOverwrite: false, applyToAll: false });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Dosya Çakışması</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
          "{file?.name}" dosyası zaten mevcut. Ne yapmak istersiniz?
        </Typography>
        
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {/* Mevcut Resim */}
          <Grid item xs={6}>
            <Typography variant="subtitle2" gutterBottom>
              Mevcut Resim:
            </Typography>
            {loading ? (
              <Box
                sx={{
                  width: '100%',
                  height: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <CircularProgress />
              </Box>
            ) : existingImageUrl ? (
              <Box
                component="img"
                src={existingImageUrl}
                alt="Mevcut resim"
                sx={{
                  width: '100%',
                  height: 300,
                  objectFit: 'contain',
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Typography color="text.secondary">Resim yüklenemedi</Typography>
              </Box>
            )}
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                Boyut: {existingFile && (existingFile.size / 1024).toFixed(2)} KB
              </Typography>
              <Typography variant="body2">
                Boyutlar: {existingFile?.width}x{existingFile?.height}
              </Typography>
              <Typography variant="body2">
                Yükleme Tarihi: {existingFile && new Date(existingFile.uploadDate).toLocaleString('tr-TR')}
              </Typography>
            </Box>
          </Grid>

          {/* Yeni Resim */}
          <Grid item xs={6}>
            <Typography variant="subtitle2" gutterBottom>
              Yeni Resim:
            </Typography>
            {newImageUrl ? (
              <Box
                component="img"
                src={newImageUrl}
                alt="Yeni resim"
                sx={{
                  width: '100%',
                  height: 300,
                  objectFit: 'contain',
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: 300,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Typography color="text.secondary">Resim yüklenemedi</Typography>
              </Box>
            )}
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                Boyut: {file && (file.size / 1024).toFixed(2)} KB
              </Typography>
              <Typography variant="body2">
                Boyutlar: {newImageDimensions ? `${newImageDimensions.width}x${newImageDimensions.height}` : '-'}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <FormControlLabel
          control={
            <Checkbox
              checked={applyToAll}
              onChange={(e) => setApplyToAll(e.target.checked)}
            />
          }
          label="Bu kararı tüm çakışan dosyalar için uygula"
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSkip}>Atla</Button>
        <Button onClick={handleOverwrite} color="primary" variant="contained">
          Üzerine Yaz
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function MainApp() {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [images, setImages] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [skuImages, setSkuImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [imageToEdit, setImageToEdit] = useState(null);
  const [editForm, setEditForm] = useState({
    sku: '',
    idx: ''
  });
  const [filters, setFilters] = useState({
    search: '',
    sku: '',
    minWidth: '',
    maxWidth: '',
    minHeight: '',
    maxHeight: '',
    minSize: '',
    maxSize: '',
    uploadDateStart: '',
    uploadDateEnd: '',
    sortBy: 'id',
    sortOrder: 'DESC'
  });
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  const [sortFields, setSortFields] = useState([
    { field: '', order: 'ASC' }
  ]);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [showErrorList, setShowErrorList] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    code: '',
    description: ''
  });
  const [projectSelectDialogOpen, setProjectSelectDialogOpen] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [currentUploadingFile, setCurrentUploadingFile] = useState('');
  const [conflictFile, setConflictFile] = useState(null);
  const [existingFile, setExistingFile] = useState(null);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [applyToAll, setApplyToAll] = useState(false);
  const [shouldOverwrite, setShouldOverwrite] = useState(false);
  const [conflictDialogCallback, setConflictDialogCallback] = useState(null);
  const fileInputRef = React.useRef();
  const [imageUrls, setImageUrls] = useState({});
  const [conflictDialogClose, setConflictDialogClose] = useState(null);
  const [newProjectDialogOpen, setNewProjectDialogOpen] = useState(false);
  const [newProjectData, setNewProjectData] = useState({
    name: '',
    code: '',
    description: ''
  });

  // Kullanıcı menüsü işlemleri
  const handleUserMenuClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
  };

  // Kullanıcı yönetimi sayfasına yönlendirme
  const handleUsersClick = () => {
    navigate('/users');
  };

  // Rol yönetimi sayfasına yönlendirme
  const handleRolesClick = () => {
    navigate('/roles');
  };

  // Debug için kullanıcı bilgilerini konsola yazdır
  useEffect(() => {
    console.log('Kullanıcı:', user);
    console.log('USER_MANAGEMENT yetkisi:', hasPermission('USER_MANAGEMENT'));
  }, [user, hasPermission]);

  // Proje değiştirme işlemi
  const handleProjectChange = (project) => {
    setImages([]); // Önce resimleri temizle
    setImageUrls({}); // URL'leri temizle
    setSelectedProject(project); // Sonra yeni projeyi seç
  };

  // selectedProject değiştiğinde resimleri getir
  useEffect(() => {
    if (selectedProject) {
      console.log('Seçilen proje güncellendi:', selectedProject);
      fetchImages();
    }
  }, [selectedProject]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resimleri getir
  const fetchImages = async () => {
    if (!selectedProject) return;

    try {
      const response = await axios.get(`/projects/${selectedProject.id}/images`, {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          ...filters
        }
      });
      
      if (response.data.images) {
        setImages(response.data.images);
        setTotalCount(response.data.totalCount);
        setStats(response.data.stats);
      } else {
        setImages([]);
        setTotalCount(0);
        setStats(null);
      }
    } catch (err) {
      console.error('Resim listeleme hatası:', err);
      setError('Resimler yüklenirken hata oluştu: ' + (err.response?.data?.message || err.message));
    }
  };

  // Images state'i değiştiğinde log'la
  useEffect(() => {
    console.log('Resimler güncellendi:', images);
  }, [images]);

  // Sayfa değiştiğinde resimleri yeniden getir
  useEffect(() => {
    if (selectedProject) {
      fetchImages();
    }
  }, [page, rowsPerPage, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  // Projeleri getir
  const fetchProjects = async () => {
    try {
      const response = await axios.get('/projects');
      // Eğer kullanıcının MANAGE_PROJECTS yetkisi yoksa, sadece yetkili olduğu projeleri göster
      if (!hasPermission('MANAGE_PROJECTS')) {
        setProjects(response.data.filter(project => project.hasAccess));
      } else {
        setProjects(response.data);
      }
    } catch (err) {
      setError('Projeler yüklenirken hata oluştu: ' + err.message);
    }
  };

  // Component mount olduğunda projeleri getir
  useEffect(() => {
    fetchProjects();
  }, []);

  // Sayfalama işleyicileri
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // getImageUrl fonksiyonunu useCallback ile sarmalayalım
  const getImageUrl = useCallback(async (uuid) => {
    if (!selectedProject) return null;
    
    try {
      const response = await axios.get(`/projects/${selectedProject.id}/images/${uuid}`, {
        responseType: 'blob'
      });
      return URL.createObjectURL(response.data);
    } catch (err) {
      console.error('Resim yükleme hatası:', err);
      return null;
    }
  }, [selectedProject]);

  // Resim URL'lerini güncelle
  useEffect(() => {
    const loadImageUrls = async () => {
      if (!selectedProject || !images || images.length === 0) return;

      const newUrls = {};
      for (const image of images) {
        if (!imageUrls[image.uuid]) { // Sadece yüklenmemiş URL'leri yükle
          try {
            newUrls[image.uuid] = await getImageUrl(image.uuid);
          } catch (err) {
            console.error('Resim yükleme hatası:', err);
            newUrls[image.uuid] = null;
          }
        }
      }
      
      if (Object.keys(newUrls).length > 0) {
        setImageUrls(prev => ({ ...prev, ...newUrls }));
      }
    };
    
    loadImageUrls();
  }, [selectedProject, images, getImageUrl, imageUrls]);

  const handleConflictDialogClose = (result) => {
    setConflictDialogOpen(false);
    setConflictFile(null);
    setExistingFile(null);
    if (conflictDialogCallback) {
      conflictDialogCallback(result);
    }
  };

  const handleFileSelect = async (event) => {
    try {
      const files = Array.from(event.target.files);
      if (files.length === 0) return;

      // Yükleme durumu için state'leri sıfırla
      setIsUploading(true);
      setUploadErrors([]);
      setUploadProgress(0);
      setApplyToAll(false);
      setShouldOverwrite(false);

      let shouldOverwriteAll = null;
      let applyToAllDecision = false;
      
      // Yükleme istatistikleri
      let stats = {
        total: files.length,
        uploaded: 0,
        skipped: 0,
        overwritten: 0,
        failed: 0
      };

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setCurrentUploadingFile(file.name);
        setUploadProgress(Math.round((i / files.length) * 100));

        try {
          // Dosya çakışma kontrolü
          const checkResponse = await axios.get(`/projects/${selectedProject.id}/images/check/${file.name}`);

          if (checkResponse.data.exists) {
            if (shouldOverwriteAll === null || !applyToAllDecision) {
              setConflictFile(file);
              setExistingFile(checkResponse.data.file);

              const result = await new Promise((resolve) => {
                setConflictDialogCallback(() => resolve);
                setConflictDialogOpen(true);
              });

              shouldOverwriteAll = result.shouldOverwrite;
              applyToAllDecision = result.applyToAll;
            }

            if (!shouldOverwriteAll) {
              stats.skipped++;
              // setUploadErrors(prev => [...prev, {
              //   file: file.name,
              //   type: 'info',
              //   message: 'Dosya zaten mevcut olduğu için atlandı'
              // }]);
              continue;
            } else {
              stats.overwritten++;
            }
          }

          // Dosyayı yükle
          const formData = new FormData();
          formData.append('images', file);
          
          await axios.post(`/projects/${selectedProject.id}/upload`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });

          stats.uploaded++;

        } catch (error) {
          console.error('Dosya yükleme hatası:', error);
          stats.failed++;
          setUploadErrors(prev => [...prev, {
            file: file.name,
            type: 'error',
            message: error.message
          }]);
        }
      }

      // Yükleme özeti
      setUploadErrors(prev => [
        ...prev,
        {
          file: 'Yükleme Özeti',
          type: 'info',
          message: `Toplam ${stats.total} dosya işlendi:
            ${stats.uploaded - stats.overwritten > 0 ? `• ${stats.uploaded - stats.overwritten} yeni dosya yüklendi` : ''}
            ${stats.skipped > 0 ? `• ${stats.skipped} dosya atlandı` : ''}
            ${stats.overwritten > 0 ? `• ${stats.overwritten} dosyanın üzerine yazıldı` : ''}
            ${stats.failed > 0 ? `• ${stats.failed} dosya yüklenemedi` : ''}`
        }
      ]);

      // State'leri sıfırla
      setIsUploading(false);
      setCurrentUploadingFile('');
      setUploadProgress(100);
      setConflictDialogOpen(false);
      setConflictFile(null);
      setExistingFile(null);
      setApplyToAll(false);
      setShouldOverwrite(false);
      setConflictDialogCallback(null);
      
      // Resim listesini yenile
      fetchImages();

      // Input'u sıfırla
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
    } catch (error) {
      console.error('Genel hata:', error);
      setUploadErrors(prev => [...prev, {
        file: 'Sistem Hatası',
        type: 'error',
        message: error.message
      }]);
      setIsUploading(false);
      setCurrentUploadingFile('');
      setUploadProgress(0);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Yeni proje oluşturma fonksiyonu
  const handleCreateProject = async () => {
    try {
      await axios.post('/projects', newProjectData);
      setSuccess(true);
      setNewProjectDialogOpen(false);
      setNewProjectData({ name: '', code: '', description: '' });
      fetchProjects();
    } catch (err) {
      setError('Proje oluşturulurken hata oluştu: ' + err.message);
    }
  };

  // Resim önizleme dialog'u açıldığında aynı SKU'ya sahip resimleri getir
  const fetchSkuImages = async (sku, projectId) => {
    try {
      const response = await axios.get(`/projects/${projectId}/images`, {
        params: {
          sku: sku,
          limit: 100 // Tüm resimleri almak için yüksek bir limit
        }
      });
      // Resimleri index'e göre sırala
      const sortedImages = response.data.images.sort((a, b) => a.idx - b.idx);
      setSkuImages(sortedImages);
      // Seçili resmin index'ini bul
      const currentIndex = sortedImages.findIndex(img => img.id === selectedImage.id);
      setCurrentImageIndex(currentIndex);
    } catch (err) {
      console.error('SKU resimleri getirme hatası:', err);
    }
  };

  // Preview açıldığında SKU resimlerini getir
  useEffect(() => {
    if (previewOpen && selectedImage && selectedProject) {
      fetchSkuImages(selectedImage.sku, selectedProject.id);
    }
  }, [previewOpen, selectedImage, selectedProject]);

  return (
    <>
      <AppBar position="static" sx={{ mb: 3 }}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            CDN Resim Yönetimi
          </Typography>
          {selectedProject && (
            <>
              <Typography variant="subtitle1" sx={{ mr: 2 }}>
                Proje: 
              </Typography>
              <FormControl sx={{ minWidth: 200, mr: 2 }} size="small">
                <Select
                  value={selectedProject.id}
                  onChange={(e) => {
                    const project = projects.find(p => p.id === e.target.value);
                    console.log('Seçilen proje:', project);
                    handleProjectChange(project);
                  }}
                >
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          <IconButton
            color="inherit"
            onClick={handleUserMenuClick}
          >
            <AccountCircleIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Kullanıcı Menüsü */}
      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
      >
        <MenuItem disabled>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary={user.fullName}
            secondary={user.role}
          />
        </MenuItem>
        {hasPermission('USER_MANAGEMENT') && (
          <>
            <MenuItem onClick={handleUsersClick}>
              <ListItemIcon>
                <PeopleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Kullanıcı Yönetimi" />
            </MenuItem>
            <MenuItem onClick={handleRolesClick}>
              <ListItemIcon>
                <VpnKeyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Rol Yönetimi" />
            </MenuItem>
            <MenuItem onClick={() => navigate('/project-access')}>
              <ListItemIcon>
                <FolderIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Proje Erişim Yönetimi" />
            </MenuItem>
          </>
        )}
        <MenuItem onClick={() => {
          handleUserMenuClose();
          setChangePasswordOpen(true);
        }}>
          <ListItemIcon>
            <VpnKeyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Şifre Değiştir" />
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <ExitToAppIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Çıkış Yap" />
        </MenuItem>
      </Menu>

      {/* Şifre Değiştirme Dialog'u */}
      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />

      <Container maxWidth="lg">
        {/* Proje Seçimi */}
        {!selectedProject && (
          <Box sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" gutterBottom>
                Proje Seçin
              </Typography>
              {hasPermission('CREATE_PROJECT') && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setNewProjectDialogOpen(true)}
                >
                  Yeni Proje
                </Button>
              )}
            </Box>
            <Grid container spacing={2}>
              {projects.map((project) => (
                <Grid item xs={12} sm={6} md={4} key={project.id}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {project.name}
                      </Typography>
                      <Typography color="text.secondary" gutterBottom>
                        Kod: {project.code}
                      </Typography>
                      {project.description && (
                        <Typography variant="body2">
                          {project.description}
                        </Typography>
                      )}
                    </CardContent>
                    <Box sx={{ p: 2, pt: 0 }}>
                      <Button
                        variant="contained"
                        onClick={() => handleProjectChange(project)}
                        fullWidth
                      >
                        Projeyi Seç
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Yeni Proje Dialog'u */}
        <Dialog
          open={newProjectDialogOpen}
          onClose={() => setNewProjectDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Yeni Proje Oluştur</DialogTitle>
          <DialogContent>
            <Box component="form" sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Proje Adı"
                value={newProjectData.name}
                onChange={(e) => setNewProjectData({ ...newProjectData, name: e.target.value })}
                margin="normal"
                required
              />
              <TextField
                fullWidth
                label="Proje Kodu"
                value={newProjectData.code}
                onChange={(e) => setNewProjectData({ ...newProjectData, code: e.target.value })}
                margin="normal"
                required
                helperText="Benzersiz bir kod giriniz"
              />
              <TextField
                fullWidth
                label="Açıklama"
                value={newProjectData.description}
                onChange={(e) => setNewProjectData({ ...newProjectData, description: e.target.value })}
                margin="normal"
                multiline
                rows={4}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNewProjectDialogOpen(false)}>
              İptal
            </Button>
            <Button
              onClick={handleCreateProject}
              variant="contained"
              disabled={!newProjectData.name || !newProjectData.code}
            >
              Oluştur
            </Button>
          </DialogActions>
        </Dialog>

        {/* Resim Yükleme Butonu */}
        {selectedProject && hasPermission('UPLOAD_IMAGE') && (
          <Box sx={{ mb: 3 }}>
            <input
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
              ref={fileInputRef}
            />
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => fileInputRef.current.click()}
              disabled={isUploading}
            >
              {isUploading ? 'Yükleniyor...' : 'Resim Yükle'}
            </Button>
            {isUploading && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  {currentUploadingFile} yükleniyor... ({uploadProgress}%)
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}
          </Box>
        )}

        {/* Yükleme Durumu ve Hataları */}
        {uploadErrors.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Alert 
              severity="info" 
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => setUploadErrors([])}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              <Typography variant="subtitle1" gutterBottom>
                Yükleme Raporu:
              </Typography>
              <List dense>
                {uploadErrors.map((error, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={error.file}
                      secondary={error.message}
                      sx={{
                        '& .MuiListItemText-primary': {
                          color: error.type === 'error' ? 'error.main' : 'text.primary',
                          fontWeight: error.type === 'error' ? 'bold' : 'normal'
                        }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          </Box>
        )}

        {/* Resim Yönetimi */}
        {selectedProject && (
          <>
            {/* Üst Toolbar */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h5" gutterBottom>
                  Resimler
                </Typography>
                {stats && (
                  <Typography variant="body2" color="text.secondary">
                    Toplam: {stats.totalImages} resim, {stats.uniqueSkus} benzersiz SKU
                  </Typography>
                )}
              </Box>
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={() => setFilterDialogOpen(true)}
                  sx={{ mr: 1 }}
                >
                  Filtrele
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SortIcon />}
                  onClick={() => setSortDialogOpen(true)}
                >
                  Sırala
                </Button>
              </Box>
            </Box>

            {/* Resim Listesi */}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Önizleme</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Dosya Adı</TableCell>
                    <TableCell>Boyutlar</TableCell>
                    <TableCell>Dosya Boyutu</TableCell>
                    <TableCell>Yükleme Tarihi</TableCell>
                    <TableCell>İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {images.map((image) => (
                    <TableRow key={image.id}>
                      <TableCell>
                        <Box
                          component="img"
                          src={imageUrls[image.uuid] || ''}
                          alt={image.filename}
                          sx={{ width: 50, height: 50, objectFit: 'contain' }}
                          onError={(e) => {
                            console.error('Resim yükleme hatası:', e);
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJmZWF0aGVyIGZlYXRoZXItaW1hZ2UiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDE2IDEwIDUgMjEiPjwvcG9seWxpbmU+PC9zdmc+';
                          }}
                        />
                      </TableCell>
                      <TableCell>{image.sku}</TableCell>
                      <TableCell>{image.filename}</TableCell>
                      <TableCell>{image.width} x {image.height}</TableCell>
                      <TableCell>{Math.round(image.size / 1024)} KB</TableCell>
                      <TableCell>
                        {new Date(image.uploadDate).toLocaleString('tr-TR')}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedImage(image);
                            setPreviewOpen(true);
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                        {hasPermission('EDIT_IMAGE') && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setImageToEdit(image);
                              setEditDialogOpen(true);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                        {hasPermission('DELETE_IMAGE') && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setImageToDelete(image);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Sayfa başına satır:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} / ${count}`
                }
              />
            </TableContainer>
          </>
        )}

        {/* Resim Önizleme Dialog'u */}
        <Dialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography>
                {selectedImage?.filename} 
                {skuImages.length > 0 && ` (${currentImageIndex + 1}/${skuImages.length})`}
              </Typography>
              <IconButton
                onClick={() => setPreviewOpen(false)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedImage && (
              <>
                <Box sx={{ position: 'relative', mb: 2 }}>
                  <Box
                    component="img"
                    src={selectedImage ? imageUrls[selectedImage.uuid] || '' : ''}
                    alt={selectedImage?.filename}
                    sx={{ width: '100%', height: 'auto', maxHeight: '70vh', objectFit: 'contain' }}
                    onError={(e) => {
                      console.error('Resim yükleme hatası:', e);
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJmZWF0aGVyIGZlYXRoZXItaW1hZ2UiPjxyZWN0IHg9IjMiIHk9IjMiIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgcng9IjIiIHJ5PSIyIj48L3JlY3Q+PGNpcmNsZSBjeD0iOC41IiBjeT0iOC41IiByPSIxLjUiPjwvY2lyY2xlPjxwb2x5bGluZSBwb2ludHM9IjIxIDE1IDE2IDEwIDUgMjEiPjwvcG9seWxpbmU+PC9zdmc+';
                    }}
                  />
                  {skuImages.length > 1 && (
                    <>
                      <IconButton
                        sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)' }}
                        onClick={() => {
                          const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : skuImages.length - 1;
                          setCurrentImageIndex(newIndex);
                          setSelectedImage(skuImages[newIndex]);
                        }}
                      >
                        <NavigateBeforeIcon />
                      </IconButton>
                      <IconButton
                        sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
                        onClick={() => {
                          const newIndex = currentImageIndex < skuImages.length - 1 ? currentImageIndex + 1 : 0;
                          setCurrentImageIndex(newIndex);
                          setSelectedImage(skuImages[newIndex]);
                        }}
                      >
                        <NavigateNextIcon />
                      </IconButton>
                    </>
                  )}
                </Box>
                <Typography variant="subtitle1" gutterBottom>
                  Dosya Adı: {selectedImage.filename}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  SKU: {selectedImage.sku}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Boyutlar: {selectedImage.width} x {selectedImage.height}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  Dosya Boyutu: {Math.round(selectedImage.size / 1024)} KB
                </Typography>
                <Typography variant="body2">
                  Yükleme Tarihi: {new Date(selectedImage.uploadDate).toLocaleString('tr-TR')}
                </Typography>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Resim Silme Dialog'u */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Resmi Sil</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Bu resmi silmek istediğinizden emin misiniz?
              Bu işlem geri alınamaz.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>
              İptal
            </Button>
            <Button
              onClick={async () => {
                try {
                  await axios.delete(`/images/${imageToDelete.id}`);
                  setSuccess(true);
                  setDeleteDialogOpen(false);
                  fetchImages();
                } catch (err) {
                  setError('Resim silinirken bir hata oluştu: ' + err.message);
                }
              }}
              color="error"
              variant="contained"
            >
              Sil
            </Button>
          </DialogActions>
        </Dialog>

        {/* Resim Düzenleme Dialog'u */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
        >
          <DialogTitle>Resmi Düzenle</DialogTitle>
          <DialogContent>
            <Box component="form" sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="SKU"
                value={editForm.sku}
                onChange={(e) => setEditForm({ ...editForm, sku: e.target.value })}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Sıra No"
                type="number"
                value={editForm.idx}
                onChange={(e) => setEditForm({ ...editForm, idx: e.target.value })}
                margin="normal"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>
              İptal
            </Button>
            <Button
              onClick={async () => {
                try {
                  await axios.put(`/images/${imageToEdit.id}`, editForm);
                  setSuccess(true);
                  setEditDialogOpen(false);
                  fetchImages();
                } catch (err) {
                  setError('Resim güncellenirken bir hata oluştu: ' + err.message);
                }
              }}
              variant="contained"
            >
              Kaydet
            </Button>
          </DialogActions>
        </Dialog>

        {/* Filtreleme Dialog'u */}
        <Dialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Filtreleme</DialogTitle>
          <DialogContent>
            <Box component="form" sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Arama"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                margin="normal"
                helperText="Dosya adı veya SKU'da ara"
              />
              <TextField
                fullWidth
                label="SKU"
                value={filters.sku}
                onChange={(e) => setFilters({ ...filters, sku: e.target.value })}
                margin="normal"
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Min. Genişlik"
                    type="number"
                    value={filters.minWidth}
                    onChange={(e) => setFilters({ ...filters, minWidth: e.target.value })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Max. Genişlik"
                    type="number"
                    value={filters.maxWidth}
                    onChange={(e) => setFilters({ ...filters, maxWidth: e.target.value })}
                    margin="normal"
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Min. Yükseklik"
                    type="number"
                    value={filters.minHeight}
                    onChange={(e) => setFilters({ ...filters, minHeight: e.target.value })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Max. Yükseklik"
                    type="number"
                    value={filters.maxHeight}
                    onChange={(e) => setFilters({ ...filters, maxHeight: e.target.value })}
                    margin="normal"
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Min. Boyut (KB)"
                    type="number"
                    value={filters.minSize}
                    onChange={(e) => setFilters({ ...filters, minSize: e.target.value })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Max. Boyut (KB)"
                    type="number"
                    value={filters.maxSize}
                    onChange={(e) => setFilters({ ...filters, maxSize: e.target.value })}
                    margin="normal"
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Başlangıç Tarihi"
                    type="date"
                    value={filters.uploadDateStart}
                    onChange={(e) => setFilters({ ...filters, uploadDateStart: e.target.value })}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Bitiş Tarihi"
                    type="date"
                    value={filters.uploadDateEnd}
                    onChange={(e) => setFilters({ ...filters, uploadDateEnd: e.target.value })}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setFilters({
                  search: '',
                  sku: '',
                  minWidth: '',
                  maxWidth: '',
                  minHeight: '',
                  maxHeight: '',
                  minSize: '',
                  maxSize: '',
                  uploadDateStart: '',
                  uploadDateEnd: '',
                  sortBy: 'id',
                  sortOrder: 'DESC'
                });
                setFilterDialogOpen(false);
              }}
            >
              Sıfırla
            </Button>
            <Button onClick={() => setFilterDialogOpen(false)}>
              İptal
            </Button>
            <Button
              onClick={() => {
                setPage(0);
                setFilterDialogOpen(false);
              }}
              variant="contained"
            >
              Uygula
            </Button>
          </DialogActions>
        </Dialog>

        {/* Sıralama Dialog'u */}
        <Dialog
          open={sortDialogOpen}
          onClose={() => setSortDialogOpen(false)}
        >
          <DialogTitle>Sıralama</DialogTitle>
          <DialogContent>
            <List>
              {sortFields.map((field, index) => (
                <ListItem key={index}>
                  <FormControl sx={{ mr: 1, minWidth: 120 }}>
                    <InputLabel>Alan</InputLabel>
                    <Select
                      value={field.field}
                      onChange={(e) => {
                        const newFields = [...sortFields];
                        newFields[index].field = e.target.value;
                        setSortFields(newFields);
                      }}
                      label="Alan"
                    >
                      <MenuItem value="id">ID</MenuItem>
                      <MenuItem value="filename">Dosya Adı</MenuItem>
                      <MenuItem value="sku">SKU</MenuItem>
                      <MenuItem value="size">Boyut</MenuItem>
                      <MenuItem value="width">Genişlik</MenuItem>
                      <MenuItem value="height">Yükseklik</MenuItem>
                      <MenuItem value="uploadDate">Yükleme Tarihi</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl sx={{ minWidth: 120 }}>
                    <InputLabel>Sıralama</InputLabel>
                    <Select
                      value={field.order}
                      onChange={(e) => {
                        const newFields = [...sortFields];
                        newFields[index].order = e.target.value;
                        setSortFields(newFields);
                      }}
                      label="Sıralama"
                    >
                      <MenuItem value="ASC">Artan</MenuItem>
                      <MenuItem value="DESC">Azalan</MenuItem>
                    </Select>
                  </FormControl>
                  <IconButton
                    onClick={() => {
                      const newFields = sortFields.filter((_, i) => i !== index);
                      setSortFields(newFields);
                    }}
                    disabled={sortFields.length === 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setSortFields([{ field: '', order: 'ASC' }]);
                setFilters({ ...filters, sortBy: 'id', sortOrder: 'DESC' });
                setSortDialogOpen(false);
              }}
            >
              Sıfırla
            </Button>
            <Button onClick={() => setSortDialogOpen(false)}>
              İptal
            </Button>
            <Button
              onClick={() => {
                const validFields = sortFields.filter(f => f.field);
                if (validFields.length > 0) {
                  setFilters({
                    ...filters,
                    sortBy: validFields.map(f => f.field).join(','),
                    sortOrder: validFields.map(f => f.order).join(',')
                  });
                }
                setSortDialogOpen(false);
              }}
              variant="contained"
            >
              Uygula
            </Button>
          </DialogActions>
        </Dialog>

        {/* Çakışma Dialog'u */}
        <ConflictDialog
          open={conflictDialogOpen}
          onClose={handleConflictDialogClose}
          file={conflictFile}
          existingFile={existingFile}
          applyToAll={applyToAll}
          setApplyToAll={setApplyToAll}
          setShouldOverwrite={setShouldOverwrite}
          selectedProject={selectedProject}
        />

        {/* Hata Mesajı */}
        <Snackbar
          open={Boolean(error)}
          autoHideDuration={6000}
          onClose={() => setError(null)}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>

        {/* Başarı Mesajı */}
        <Snackbar
          open={success}
          autoHideDuration={6000}
          onClose={() => setSuccess(false)}
        >
          <Alert severity="success" onClose={() => setSuccess(false)}>
            İşlem başarıyla tamamlandı
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
}

export default MainApp; 